import { useCallback, useEffect, useRef } from "react";
import { recordWantAdImpressions } from "@/services/v2/wantAdService";

export const VISIBLE_RATIO = 0.5;
export const DWELL_MS = 1000;
export const FLUSH_MS = 10000;
export const MAX_BATCH = 100;

export const useImpressionTracker = ({
  surface,
  enabled = true,
  requireScroll = false,
  payload = null,
  send = recordWantAdImpressions,
}) => {
  const observerRef = useRef(null);
  const nodeIdsRef = useRef(new Map());
  const refCallbacksRef = useRef(new Map());
  const visibleRef = useRef(new Set());
  const timersRef = useRef(new Map());
  const seenRef = useRef(new Set());
  const pendingRef = useRef(new Set());
  const armedRef = useRef(!requireScroll);
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const flush = useCallback(() => {
    if (!pendingRef.current.size) return;
    const wantAdIds = [...pendingRef.current];
    pendingRef.current.clear();
    for (let i = 0; i < wantAdIds.length; i += MAX_BATCH) {
      send({
        ...(payloadRef.current || {}),
        wantAdIds: wantAdIds.slice(i, i + MAX_BATCH),
        surface,
      });
    }
  }, [send, surface]);

  const clearTimer = useCallback((el) => {
    const t = timersRef.current.get(el);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(el);
    }
  }, []);

  const startTimer = useCallback((el) => {
    const timers = timersRef.current;
    const wantAdId = nodeIdsRef.current.get(el);
    if (!wantAdId || seenRef.current.has(wantAdId) || timers.has(el)) return;
    timers.set(
      el,
      setTimeout(() => {
        timers.delete(el);
        if (document.visibilityState === "hidden") return;
        if (!visibleRef.current.has(el) || !armedRef.current) return;
        if (nodeIdsRef.current.get(el) !== wantAdId) return;
        seenRef.current.add(wantAdId);
        pendingRef.current.add(wantAdId);
      }, DWELL_MS),
    );
  }, []);

  useEffect(() => {
    if (!enabled || typeof IntersectionObserver === "undefined") return undefined;

    const timers = timersRef.current;
    const visible = visibleRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (!nodeIdsRef.current.has(el)) return;
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
            visible.add(el);
            if (armedRef.current) startTimer(el);
          } else {
            visible.delete(el);
            clearTimer(el);
          }
        });
      },
      { threshold: [0, VISIBLE_RATIO, 1] },
    );
    observerRef.current = observer;
    nodeIdsRef.current.forEach((_id, node) => observer.observe(node));

    const onScroll = () => {
      if (armedRef.current) return;
      armedRef.current = true;
      visible.forEach((el) => startTimer(el));
    };
    if (!armedRef.current) {
      document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    }

    const interval = setInterval(flush, FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") {
        visible.forEach((el) => clearTimer(el));
        flush();
        return;
      }
      if (armedRef.current) visible.forEach((el) => startTimer(el));
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flush);

    return () => {
      clearInterval(interval);
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flush);
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      visible.clear();
      observer.disconnect();
      observerRef.current = null;
      flush();
    };
  }, [enabled, flush, startTimer, clearTimer]);

  const detach = useCallback(
    (node) => {
      clearTimer(node);
      visibleRef.current.delete(node);
      nodeIdsRef.current.delete(node);
      if (observerRef.current) observerRef.current.unobserve(node);
    },
    [clearTimer],
  );

  const attach = useCallback((node, id) => {
    nodeIdsRef.current.set(node, id);
    if (observerRef.current) observerRef.current.observe(node);
  }, []);

  return useCallback(
    (wantAdId, slot = "") => {
      const id = Number(wantAdId);
      if (!id) return undefined;
      const cacheKey = `${slot}:${id}`;
      const cached = refCallbacksRef.current.get(cacheKey);
      if (cached) return cached;
      let current = null;
      const refCallback = (node) => {
        if (current && current !== node) detach(current);
        current = node || null;
        if (node) {
          attach(node, id);
        } else {
          refCallbacksRef.current.delete(cacheKey);
        }
      };
      refCallbacksRef.current.set(cacheKey, refCallback);
      return refCallback;
    },
    [attach, detach],
  );
};

export default useImpressionTracker;
